import { useCallback, useEffect, useState } from "react";

/**
 * Hook que gestiona el estado del modo edición: campos del formulario,
 * datos iniciales, detección de cambios, y timer de carga inicial.
 */
export function useEditModeState({
  isEditMode,
  opportunityData,
  inventarioData,
  plazo,
  frecuencia,
  horasSeleccionadas,
  duracion,
  isProgrammaticSelected,
  inversionFinalTotal,
}) {
  // --- Estados del formulario de edición ---
  const [editMesCierre, setEditMesCierre] = useState("");
  const [editAnoCierre, setEditAnoCierre] = useState("");
  const [editMesFactura, setEditMesFactura] = useState("");
  const [editAnoFactura, setEditAnoFactura] = useState("");
  const [editEstatus, setEditEstatus] = useState("0%");

  // --- Estados de inicialización y cambios ---
  const [initialData, setInitialData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasProposalChanges, setHasProposalChanges] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [originalDiscountWasReset, setOriginalDiscountWasReset] = useState(false);

  // --- Cargar valores del formulario desde opportunityData ---
  useEffect(() => {
    if (isEditMode && opportunityData) {
      const mesCierreValue = opportunityData.mes_cierre
        ? String(parseInt(opportunityData.mes_cierre.split("-")[0], 10))
        : "";
      const anoCierreValue = opportunityData.mes_cierre
        ? opportunityData.mes_cierre.split("-")[1]
        : opportunityData.ano || new Date().getFullYear().toString();
      const mesFacturaValue = opportunityData.mes_factura
        ? String(parseInt(opportunityData.mes_factura.split("-")[0], 10))
        : "";
      const anoFacturaValue = opportunityData.mes_factura
        ? opportunityData.mes_factura.split("-")[1]
        : opportunityData.ano || new Date().getFullYear().toString();

      setEditMesCierre(mesCierreValue);
      setEditAnoCierre(anoCierreValue);
      setEditMesFactura(mesFacturaValue);
      setEditAnoFactura(anoFacturaValue);
      setEditEstatus(opportunityData.estatus || "0%");
    }
  }, [isEditMode, opportunityData]);

  // --- Detectar cambios solo en la propuesta comercial (sin campos de formulario) ---
  const calculateProposalChanges = useCallback(() => {
    if (!initialData || !isInitialized) return false;

    const edificiosActualesArray = [
      ...(inventarioData.edificiosSeleccionadosAscensores || []),
      ...(inventarioData.edificiosSeleccionadosTotems || []),
      ...(inventarioData.edificiosSeleccionadosCowork || []),
      ...(inventarioData.edificiosSeleccionadosEspera || []),
    ].filter(Boolean);
    const uniqueEdificiosActualesArray = [...new Set(edificiosActualesArray)].sort((a, b) => a - b);
    const edificiosActuales = uniqueEdificiosActualesArray.join(",");

    const edificiosInicialesArray = (initialData.edificiosparticipantes || "").split(",").filter(Boolean).map(Number);
    const uniqueEdificiosInicialesArray = [...new Set(edificiosInicialesArray)].sort((a, b) => a - b);
    const edificiosIniciales = uniqueEdificiosInicialesArray.join(",");

    const ascensoresIdsActuales = (inventarioData.edificiosSeleccionadosAscensores || [])
      .sort((a, b) => a - b)
      .join(",");
    const totemsIdsActuales = (inventarioData.edificiosSeleccionadosTotems || []).sort((a, b) => a - b).join(",");
    const esperaIdsActuales = (inventarioData.edificiosSeleccionadosEspera || []).sort((a, b) => a - b).join(",");
    const coworkIdsActuales = (inventarioData.edificiosSeleccionadosCowork || []).sort((a, b) => a - b).join(",");

    const currentData = {
      edificiosparticipantes: edificiosActuales,
      pantallas_ascensores: ascensoresIdsActuales,
      pantallas_circulacion: totemsIdsActuales,
      pantallas_espera: esperaIdsActuales,
      pantallas_cowork: coworkIdsActuales,
      periodopauta: plazo || 0,
      formatopauta: frecuencia ? String(frecuencia) : "15",
      horas_pauta: horasSeleccionadas.horas || 14,
      intervalos: horasSeleccionadas.intervalos || [],
      options: horasSeleccionadas.options || [{ label: "FULL DAY | 07:00 - 21:00", hours: 14 }],
      duracion: duracion || 10,
      tipo: isProgrammaticSelected ? "p" : "d",
    };

    const proposalDifferences = {};

    if (currentData.edificiosparticipantes !== edificiosIniciales) {
      proposalDifferences.edificiosparticipantes = true;
    }
    if (currentData.pantallas_ascensores !== initialData.pantallas_ascensores) {
      proposalDifferences.pantallas_ascensores = true;
    }
    if (currentData.pantallas_circulacion !== initialData.pantallas_circulacion) {
      proposalDifferences.pantallas_circulacion = true;
    }
    if (currentData.pantallas_espera !== initialData.pantallas_espera) {
      proposalDifferences.pantallas_espera = true;
    }
    if (currentData.pantallas_cowork !== initialData.pantallas_cowork) {
      proposalDifferences.pantallas_cowork = true;
    }
    if (currentData.periodopauta !== initialData.periodopauta) {
      proposalDifferences.periodopauta = true;
    }
    if (currentData.formatopauta !== initialData.formatopauta) {
      proposalDifferences.formatopauta = true;
    }
    if (currentData.horas_pauta !== initialData.horas_pauta) {
      proposalDifferences.horas_pauta = true;
    }
    if (JSON.stringify(currentData.intervalos) !== JSON.stringify(initialData.intervalos)) {
      proposalDifferences.intervalos = true;
    }
    if (JSON.stringify(currentData.options) !== JSON.stringify(initialData.options)) {
      proposalDifferences.options = true;
    }
    if (currentData.duracion !== initialData.duracion) {
      proposalDifferences.duracion = true;
    }
    if (currentData.tipo !== initialData.tipo) {
      proposalDifferences.tipo = true;
    }

    return Object.keys(proposalDifferences).length > 0;
  }, [
    initialData,
    isInitialized,
    inventarioData,
    plazo,
    frecuencia,
    horasSeleccionadas,
    duracion,
    isProgrammaticSelected,
  ]);

  // --- Detectar cambios (propuesta + formulario) ---
  useEffect(() => {
    if (!isEditMode || !initialData || !isInitialized) {
      setHasChanges(false);
      setHasProposalChanges(false);
      return;
    }
    const edificiosActualesArray = [
      ...(inventarioData.edificiosSeleccionadosAscensores || []),
      ...(inventarioData.edificiosSeleccionadosTotems || []),
      ...(inventarioData.edificiosSeleccionadosCowork || []),
      ...(inventarioData.edificiosSeleccionadosEspera || []),
    ].filter(Boolean);
    const uniqueEdificiosActualesArray = [...new Set(edificiosActualesArray)].sort((a, b) => a - b);
    const edificiosActuales = uniqueEdificiosActualesArray.join(",");
    const edificiosInicialesArray = (initialData.edificiosparticipantes || "").split(",").filter(Boolean).map(Number);
    const uniqueEdificiosInicialesArray = [...new Set(edificiosInicialesArray)].sort((a, b) => a - b);
    const edificiosIniciales = uniqueEdificiosInicialesArray.join(",");
    const ascensoresIdsActuales = (inventarioData.edificiosSeleccionadosAscensores || [])
      .sort((a, b) => a - b)
      .join(",");
    const totemsIdsActuales = (inventarioData.edificiosSeleccionadosTotems || []).sort((a, b) => a - b).join(",");
    const esperaIdsActuales = (inventarioData.edificiosSeleccionadosEspera || []).sort((a, b) => a - b).join(",");
    const coworkIdsActuales = (inventarioData.edificiosSeleccionadosCowork || []).sort((a, b) => a - b).join(",");
    const currentData = {
      edificiosparticipantes: edificiosActuales,
      pantallas_ascensores: ascensoresIdsActuales,
      pantallas_circulacion: totemsIdsActuales,
      pantallas_espera: esperaIdsActuales,
      pantallas_cowork: coworkIdsActuales,
      periodopauta: plazo || 0,
      formatopauta: frecuencia ? String(frecuencia) : "15",
      horas_pauta: horasSeleccionadas.horas || 14,
      intervalos: horasSeleccionadas.intervalos || [],
      options: horasSeleccionadas.options || [{ label: "FULL DAY | 07:00 - 21:00", hours: 14 }],
      duracion: duracion || 10,
      tipo: isProgrammaticSelected ? "p" : "d",
      mes_cierre: editMesCierre || initialData.mes_cierre,
      ano_cierre: editAnoCierre || initialData.ano_cierre,
      mes_factura: editMesFactura || initialData.mes_factura,
      ano_factura: editAnoFactura || initialData.ano_factura,
      estatus: editEstatus || initialData.estatus,
      importe: Math.floor(inversionFinalTotal || 0),
    };
    const differences = {};
    const proposalDifferences = {};
    // Comparar campos de la propuesta
    if (edificiosActuales !== edificiosIniciales) {
      differences.edificiosparticipantes = {
        current: edificiosActuales,
        initial: edificiosIniciales,
      };
      proposalDifferences.edificiosparticipantes = differences.edificiosparticipantes;
    }
    if (currentData.pantallas_ascensores !== initialData.pantallas_ascensores) {
      differences.pantallas_ascensores = {
        current: currentData.pantallas_ascensores,
        initial: initialData.pantallas_ascensores,
      };
      proposalDifferences.pantallas_ascensores = differences.pantallas_ascensores;
    }
    if (currentData.pantallas_circulacion !== initialData.pantallas_circulacion) {
      differences.pantallas_circulacion = {
        current: currentData.pantallas_circulacion,
        initial: initialData.pantallas_circulacion,
      };
      proposalDifferences.pantallas_circulacion = differences.pantallas_circulacion;
    }
    if (currentData.pantallas_espera !== initialData.pantallas_espera) {
      differences.pantallas_espera = {
        current: currentData.pantallas_espera,
        initial: initialData.pantallas_espera,
      };
      proposalDifferences.pantallas_espera = differences.pantallas_espera;
    }
    if (currentData.pantallas_cowork !== initialData.pantallas_cowork) {
      differences.pantallas_cowork = {
        current: currentData.pantallas_cowork,
        initial: initialData.pantallas_cowork,
      };
      proposalDifferences.pantallas_cowork = differences.pantallas_cowork;
    }
    if (currentData.periodopauta !== initialData.periodopauta) {
      differences.periodopauta = {
        current: currentData.periodopauta,
        initial: initialData.periodopauta,
      };
      proposalDifferences.periodopauta = differences.periodopauta;
    }
    if (currentData.formatopauta !== initialData.formatopauta) {
      differences.formatopauta = {
        current: currentData.formatopauta,
        initial: initialData.formatopauta,
      };
      proposalDifferences.formatopauta = differences.formatopauta;
    }
    if (currentData.horas_pauta !== initialData.horas_pauta) {
      differences.horas_pauta = {
        current: currentData.horas_pauta,
        initial: initialData.horas_pauta,
      };
      proposalDifferences.horas_pauta = differences.horas_pauta;
    }
    if (JSON.stringify(currentData.intervalos) !== JSON.stringify(initialData.intervalos)) {
      differences.intervalos = {
        current: currentData.intervalos,
        initial: initialData.intervalos,
      };
      proposalDifferences.intervalos = differences.intervalos;
    }
    if (JSON.stringify(currentData.options) !== JSON.stringify(initialData.options)) {
      differences.options = {
        current: currentData.options,
        initial: initialData.options,
      };
      proposalDifferences.options = differences.options;
    }
    if (currentData.duracion !== initialData.duracion) {
      differences.duracion = {
        current: currentData.duracion,
        initial: initialData.duracion,
      };
      proposalDifferences.duracion = differences.duracion;
    }
    if (currentData.tipo !== initialData.tipo) {
      differences.tipo = {
        current: currentData.tipo,
        initial: initialData.tipo,
      };
      proposalDifferences.tipo = differences.tipo;
    }
    // Comparar campos del formulario (no afectan la propuesta)
    if (currentData.mes_cierre !== initialData.mes_cierre) {
      differences.mes_cierre = {
        current: currentData.mes_cierre,
        initial: initialData.mes_cierre,
      };
    }
    if (currentData.ano_cierre !== initialData.ano_cierre) {
      differences.ano_cierre = {
        current: currentData.ano_cierre,
        initial: initialData.ano_cierre,
      };
    }
    if (currentData.mes_factura !== initialData.mes_factura) {
      differences.mes_factura = {
        current: currentData.mes_factura,
        initial: initialData.mes_factura,
      };
    }
    if (currentData.ano_factura !== initialData.ano_factura) {
      differences.ano_factura = {
        current: currentData.ano_factura,
        initial: initialData.ano_factura,
      };
    }
    if (currentData.estatus !== initialData.estatus) {
      differences.estatus = {
        current: currentData.estatus,
        initial: initialData.estatus,
      };
    }
    // Comparar importe (solo en differences, no en proposalDifferences)
    if (Math.abs(Math.floor(currentData.importe) - Math.floor(initialData.importe)) > 0) {
      differences.importe = {
        current: currentData.importe,
        initial: initialData.importe,
      };
    }
    const hasDataChanged = Object.keys(differences).length > 0;
    const hasProposalDataChanged = Object.keys(proposalDifferences).length > 0;
    setHasChanges(hasDataChanged);
    setHasProposalChanges(hasProposalDataChanged);
  }, [
    inventarioData,
    plazo,
    frecuencia,
    horasSeleccionadas,
    duracion,
    isProgrammaticSelected,
    editMesCierre,
    editAnoCierre,
    editMesFactura,
    editAnoFactura,
    editEstatus,
    initialData,
    isInitialized,
    isEditMode,
    inversionFinalTotal,
  ]);

  // --- Timer de carga inicial (1s de estabilización) ---
  useEffect(() => {
    if (isEditMode && isInitialized && !initialLoadComplete) {
      const timer = setTimeout(() => {
        setInitialLoadComplete(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isEditMode, isInitialized, initialLoadComplete]);

  return {
    // Campos formulario
    editMesCierre,
    setEditMesCierre,
    editAnoCierre,
    setEditAnoCierre,
    editMesFactura,
    setEditMesFactura,
    editAnoFactura,
    setEditAnoFactura,
    editEstatus,
    setEditEstatus,
    // Inicialización y datos iniciales
    initialData,
    setInitialData,
    isInitialized,
    setIsInitialized,
    // Detección de cambios
    hasChanges,
    hasProposalChanges,
    calculateProposalChanges,
    // Carga inicial
    initialLoadComplete,
    // Descuento reset
    originalDiscountWasReset,
    setOriginalDiscountWasReset,
  };
}
